import { createEffect, createStore } from 'effector'
import type { TimeRange } from '../../interfaces/epg'
import type { ApiResponseError } from '../../interfaces/errors'
import type { PageParams, SortParams } from '../../interfaces/fxParams'
import { stateful } from '../../lib/stateful'
import type {
  EpgInfo,
  EpgInfoDto,
  IProductionCountry,
  LiveChannel,
  LiveChannelCategory,
  LiveChannelRegion,
  TvChannelWithEpg,
  TvEpg,
  TvEpgApiDto,
} from './index.h'

export {
  type EpgInfo,
  type EpgInfoDto,
  type IProductionCountry,
  type LiveChannel,
  type LiveChannelCategory,
  type LiveChannelRegion,
  type TimeRange,
  type TvChannelWithEpg,
  type TvEpg,
  type TvEpgApiDto,
}

type GetOneParams = { id: number }
type RegionParams = { regionId: number }
type LiveChannelCategoryParams = { id: number } // category id in path; CategoryParams is already taken
export type PlaybackUrl = { playbackUrl: string }
type CategoryParams = { withChannelIds?: 1 | 0 }
type RequestHeaders = { headers: Headers }

type FavoriteParams = { id: number }

interface LiveChannelCategories extends Array<LiveChannelCategory> {
  total: number
}

export interface LiveChannelList extends Array<LiveChannel> {
  total: number
}

export interface LiveChannelRegionList extends Array<LiveChannelRegion> {
  total: number
}

// *
// * base API effects
// *
export const base = {
  getOneFx: createEffect<GetOneParams, LiveChannel>(),
  getOnePlaybackUrlFx: createEffect<
    GetOneParams &
      RequestHeaders & {
        isRewind?: boolean
        startTime?: number
        rewind?: number
        timeshift?: number
      },
    PlaybackUrl
  >(),
  getCatchupPlaybackUrlFx: createEffect<
    GetOneParams & RequestHeaders & { startTime: number; endTime: number },
    PlaybackUrl
  >(),
  getLiveChannelsCategoriesFx: createEffect<
    RegionParams & CategoryParams,
    LiveChannelCategories
  >(),
  getFavoritesFx: createEffect<PageParams & SortParams, LiveChannelList>(),
  addToFavoritesFx: createEffect<FavoriteParams, void>(),
  removeFromFavoritesFx: createEffect<FavoriteParams, void>(),
  getEpgInformationFormattedFx: createEffect<
    {
      epgIds: string[]
      channels: LiveChannel[] | TvChannelWithEpg[]
      period: TimeRange
    },
    { epg: TvEpg; period: TimeRange }
  >(),
  getLiveChannelsRegionsFx: createEffect<SortParams, LiveChannelRegionList>(),
  getLiveChannelsByRegionFx: createEffect<
    RegionParams & PageParams & SortParams,
    LiveChannelList
  >(),
  getLiveChannelsByRegionByCategoryFx: createEffect<
    RegionParams & LiveChannelCategoryParams & PageParams & SortParams,
    LiveChannelList
  >(),
}

export const pageableFavorites = (initial?: PageParams) =>
  stateful({
    effect: base.getFavoritesFx,
    initial,
    methods: {
      limit: (n: number) => ({ limit: n }),
      offset: (n: number) => ({ offset: n }),
      next: (_: void) => ({}),
      prev: (_: void, { offset = 0, limit = 36 }) => ({
        offset: Math.max(offset - limit, 0),
      }),
    },
  })

export const error = {
  getOneFx: createStore<null | ApiResponseError>(null).on(
    base.getOneFx.fail,
    (_, { error }) => {
      // @ts-ignore because error does not exist in type Error
      const response = error?.error?.response
      if (!response) return

      return {
        code: response.status,
        message: response.statusText,
      }
    }
  ),
}
